import { useState } from 'react';
import { Link } from 'react-router';
import { useApp } from '../../app/lib/AppContext';
import { AI_ACTIONS } from '../../app/lib/credits';
import { vnShortDate } from '../../app/lib/coach';

// The blocks of the admin user page, one per key of GET /api/admin/users/:id (UserInsight). Each takes its own slice
// of the JSON; a block whose key is missing isn't rendered by the page. Counts and labels only, never content.

const LOCALE = 'vi-VN';
const num = (n) => (n == null ? '–' : Number(n).toLocaleString(LOCALE, { maximumFractionDigits: 1 }));
const dayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(LOCALE, { day: 'numeric' });
const weekday = (iso) => ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][new Date(`${iso}T00:00:00`).getDay()];
export const dateTime = (iso) => (iso ? new Date(iso).toLocaleString(LOCALE, { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'numeric', timeZone: 'Asia/Ho_Chi_Minh' }) : '');
const actionLabel = (id) => AI_ACTIONS[id]?.label || id;

export function Card({ title, aside, children, className = '' }) {
  return (
    <section className={`flex flex-col gap-3 rounded-[20px] bg-base-100 p-4 ${className}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-[15px] font-bold">{title}</h2>
        {aside && <span className="text-xs text-muted">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

function Rows({ rows }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
      {rows.filter(Boolean).map(([label, value]) => (
        <div key={label} className="contents"><dt className="text-muted">{label}</dt><dd className="min-w-0">{value}</dd></div>
      ))}
    </dl>
  );
}

function Tile({ value, label, sub, tone }) {
  return (
    <div className="rounded-[16px] bg-base-100 p-3">
      <div className={`tabular text-2xl font-extrabold ${tone || ''}`}>{value}</div>
      <div className="text-xs font-semibold">{label}</div>
      {sub && <div className="text-[11px] text-muted">{sub}</div>}
    </div>
  );
}

export function QuickStats({ stats }) {
  const { days_opened: opened, meals, workouts, protein_days: protein, streak } = stats;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
      <Tile value={opened.known || opened.value != null ? `${opened.value}/${opened.of}` : '–'} label="ngày mở app"
        sub={opened.value == null ? 'chưa có dữ liệu' : opened.known ? '14 ngày qua' : `từ khi bắt đầu ghi (${opened.of} ngày)`} tone="text-[#17804f]" />
      <Tile value={meals.total} label="bữa đã ghi" sub={`${meals.ai} bằng AI · ${meals.manual} tay`} />
      <Tile value={workouts.total} label="buổi tập" sub={`${workouts.with_coach} với Coach · ${workouts.own} tự tập`} />
      <Tile value={protein.of ? `${protein.met}/${protein.of}` : '–'} label="ngày đạt đạm" sub="trên số ngày có ghi bữa" />
      <Tile value={streak.current} label="ngày liên tiếp" sub={`kỷ lục ${streak.best}`} />
    </div>
  );
}

const SHADES = ['bg-base-200', 'bg-[#cfe8d9]', 'bg-[#7cc39b]', 'bg-[#17804f]'];
const shade = (n) => SHADES[n >= 4 ? 3 : n >= 2 ? 2 : n >= 1 ? 1 : 0];
const GRID_ROWS = [['opened', 'Mở app'], ['meals', 'Ghi bữa'], ['workouts', 'Ghi tập'], ['ai', 'Dùng AI']];

export function ActivityGrid({ grid }) {
  const { days, known, rows, summary } = grid;
  const hours = summary.meal_hours.map((h) => `${h}h`).join(' và ');
  return (
    <Card title={`Hoạt động ${days.length} ngày`} aside="đậm = nhiều">
      <div className="overflow-x-auto">
        <table className="text-[11px]">
          <thead>
            <tr><th />{days.map((d) => <th key={d} className="w-6 px-0.5 text-center font-normal text-muted">{dayLabel(d)}</th>)}</tr>
          </thead>
          <tbody>
            {GRID_ROWS.map(([key, label]) => (
              <tr key={key}>
                <th className="whitespace-nowrap pr-2 text-left font-semibold">{label}</th>
                {rows[key].map((n, i) => {
                  const unknown = key === 'opened' && !known[i];
                  return (
                    <td key={days[i]} className="px-0.5 py-0.5">
                      <span title={`${weekday(days[i])} ${vnShortDate(days[i])}: ${unknown ? 'chưa có dữ liệu' : n}`}
                        className={`block size-5 rounded-[5px] ${unknown ? 'border border-dashed border-line bg-transparent' : shade(n)}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted">
        {hours ? `Hay ghi bữa lúc ${hours}` : 'Chưa ghi bữa nào'}
        {summary.empty_days.length > 0 && ` · bỏ trống ${summary.empty_days.map((d) => `${weekday(d)} ${vnShortDate(d)}`).join(', ')}`}
        {known.some((k) => !k) && ' · ô viền đứt: chưa ghi nhận mở app'}
      </p>
    </Card>
  );
}

const TIER_NAMES = { free: 'Miễn phí', trial: 'Dùng thử', pro: 'Pro', gift: 'HLV tặng Pro', unlimited: 'Không giới hạn' };

export function CreditsBlock({ credits }) {
  const max = Math.max(1, ...credits.days.map((d) => d.used));
  return (
    <Card title="Gói & lượt AI" aside={`${TIER_NAMES[credits.tier]} · ${credits.daily == null ? 'không giới hạn' : `${credits.daily} lượt/ngày`}`}>
      <Rows rows={[
        ['Dùng thử', credits.trial_used ? 'Đã dùng' : 'Chưa dùng'],
        ['Hôm nay', credits.today.limit == null ? `${credits.today.used} lượt` : `${credits.today.used}/${credits.today.limit} lượt · làm mới 0:00`],
        ['14 ngày', <>{credits.used_total} lượt{credits.limit_hit_days > 0 && <b className="text-[#c2410c]"> · hết lượt {credits.limit_hit_days} ngày</b>}</>],
        ['AI lỗi', `${credits.failed} lần${credits.rejected ? ` · ${credits.rejected} lần không liên quan` : ''} (không trừ lượt)`],
      ]} />
      <div className="flex h-20 items-end gap-1" aria-label="Lượt AI theo ngày">
        {credits.days.map((d) => (
          <span key={d.date} title={`${vnShortDate(d.date)}: ${d.used} lượt${d.limit_hit ? ', hết lượt' : ''}`}
            className={`flex-1 rounded-t-[4px] ${d.limit_hit ? 'bg-[#e67b4b]' : 'bg-[#3b82f6]'}`}
            style={{ height: `${Math.max(4, (d.used / max) * 100)}%`, opacity: d.used ? 1 : 0.25 }} />
        ))}
      </div>
      <p className="text-xs text-muted">Cam = ngày dùng hết lượt{credits.tier === 'free' && credits.limit_hit_days > 0 ? ' → ứng viên tốt để mời dùng thử Pro' : ''}{!credits.limit_known && ' · ngày hết lượt chỉ ghi nhận từ khi bắt đầu ghi'}</p>
    </Card>
  );
}

export function AiUsesBlock({ uses }) {
  return (
    <Card title="AI dùng vào việc gì" aside="14 ngày">
      {uses.length === 0 ? <p className="text-sm text-muted">Chưa dùng AI.</p> : (
        <table className="w-full text-sm">
          <tbody className="divide-y divide-line">
            {uses.map((u) => (
              <tr key={u.action}>
                <td className="py-1.5">{actionLabel(u.action)}</td>
                <td className="tabular py-1.5 text-right font-bold">{u.count}</td>
                <td className="tabular w-14 py-1.5 text-right text-muted">{Math.round(u.share * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

const SEX = { male: 'Nam', female: 'Nữ' };
const SET_BY = { initial: 'tự đặt', manual: 'tự đặt', ai_suggestion: 'AI gợi ý', coach: 'Coach' };

export function ProfileBlock({ profile }) {
  const t = profile.targets;
  const setBy = t.set_by && (t.set_by.coach ? <>đặt bởi <Link className="underline" to={`/admin/users/${t.set_by.coach.id}`}>{t.set_by.coach.name}</Link></> : SET_BY[t.set_by.source]);
  return (
    <Card title="Hồ sơ & mục tiêu" aside={setBy}>
      <Rows rows={[
        ['Cơ thể', [SEX[profile.sex], profile.birth_year, profile.weight_kg && `${num(profile.weight_kg)} kg`, profile.height_cm && `${profile.height_cm} cm`].filter(Boolean).join(' · ') || 'Chưa nhập'],
        ['Tập', profile.training_days_per_week != null ? `${profile.training_days_per_week} buổi/tuần` : 'Chưa nhập'],
        ['Calo', t.calorie_goal ? `${num(t.calorie_goal)} kcal` : 'Chưa đặt'],
        ['Đạm', t.protein_goal ? `${t.protein_goal} g ngày nghỉ${t.workout_protein_goal ? ` · ${t.workout_protein_goal} g ngày tập` : ''}` : 'Chưa đặt'],
        ['Đổi mục tiêu', profile.changes.count ? `${profile.changes.count} lần · gần nhất ${vnShortDate(profile.changes.last_on)}` : 'Chưa đổi'],
      ]} />
    </Card>
  );
}

export function FeaturesBlock({ features }) {
  const used = features.filter((f) => f.used);
  return (
    <Card title="Tính năng đã dùng" aside="xanh = đã dùng">
      <div className="flex flex-wrap gap-1.5">
        {features.map((f) => (
          <span key={f.id} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${f.used ? 'bg-[#e3f1e9] text-[#17804f]' : 'bg-base-200 text-muted'}`}>
            {f.used ? '✓ ' : ''}{f.label}{f.used && f.count > 1 ? ` · ${f.count}` : ''}
          </span>
        ))}
      </div>
      {used.some((f) => f.detail) && (
        <Rows rows={used.filter((f) => f.detail).map((f) => [f.label, f.detail])} />
      )}
    </Card>
  );
}

const KINDS = [['', 'Tất cả'], ['meal', 'Bữa ăn'], ['workout', 'Tập'], ['ai', 'AI lỗi'], ['target', 'Mục tiêu'], ['menu', 'Thực đơn'], ['feedback', 'Góp ý'], ['coach', 'Coach']];
const TAG_STYLES = { AI: 'bg-[#e8f0fe] text-[#1a56db]', Coach: 'bg-[#e3f1e9] text-[#17804f]', Lỗi: 'bg-[#fdebe4] text-[#8e3414]' };
const FEEDBACK_STATUS = { open: 'Đang chờ', resolved: 'Đã xử lý', closed: 'Đã đóng' };

export function TimelineBlock({ userId, initial }) {
  const { api } = useApp();
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(initial);
  const [busy, setBusy] = useState(false);
  const load = async (nextKind, before) => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (nextKind) params.set('kind', nextKind);
      if (before) params.set('before', before);
      const res = await api.get(`/admin/users/${userId}/timeline?${params}`);
      setPage((prev) => (before ? { items: [...prev.items, ...res.items], next: res.next } : res));
    } finally {
      setBusy(false);
    }
  };
  const pick = (k) => { setKind(k); if (k === '' && !kind) return; load(k); };

  return (
    <Card title="Hoạt động gần đây" aside="mới nhất trước">
      <div className="flex gap-1 overflow-x-auto pb-1">
        {KINDS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => pick(id)}
            className={`h-8 shrink-0 rounded-full px-3 text-xs font-semibold ${kind === id ? 'bg-neutral text-neutral-content' : 'bg-base-200'}`}>{label}</button>
        ))}
      </div>
      {page.items.length === 0 ? <p className="text-sm text-muted">Chưa có hoạt động.</p> : (
        <ul className="flex flex-col divide-y divide-line">
          {page.items.map((it, i) => {
            const tag = it.kind === 'feedback' ? FEEDBACK_STATUS[it.tag] : it.tag;
            return (
              <li key={`${it.at}-${i}`} className="flex items-start gap-3 py-2 text-sm">
                <span className="w-24 shrink-0 text-xs text-muted">{dateTime(it.at)}</span>
                <span className="min-w-0 flex-1">{it.text}{it.action ? ` · ${actionLabel(it.action)}` : ''}</span>
                {tag && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${TAG_STYLES[tag] || 'bg-base-200 text-muted'}`}>{tag}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {page.next && (
        <button type="button" className="btn h-10 min-h-10 rounded-[12px] border-line bg-base-100 text-sm" disabled={busy} onClick={() => load(kind, page.next)}>
          {busy ? <span className="loading loading-spinner loading-sm" /> : 'Xem thêm'}
        </button>
      )}
    </Card>
  );
}

const JOINED_BY = { invited: 'link mời', self_joined: 'mã HLV' };

export function CoachSectionBlock({ section }) {
  const w = section.week;
  return (
    <Card title="Huấn luyện viên" aside="học viên">
      <Rows rows={[
        ['Coach', <><Link className="font-semibold underline" to={`/admin/users/${section.coach.id}`}>{section.coach.name}</Link> · tham gia qua {JOINED_BY[section.joined_by] || section.joined_by}{section.joined_at ? ` ${vnShortDate(section.joined_at)}` : ''}</>],
        ['Gói từ Coach', section.gift ? `Tặng Pro ${section.gift.until_cancelled ? 'đến khi dừng' : `đến ${vnShortDate(section.gift.ends_at)}`}` : 'Không (Coach chưa tặng Pro)'],
        ['Lịch hẹn tuần này', w.total ? `${w.trained}/${w.total} đã tập${w.missed ? ` · ${w.missed} vắng` : ''}${w.off ? ` · ${w.off} báo nghỉ` : ''}` : 'Không có buổi nào'],
      ]} />
    </Card>
  );
}

const NOTIFY = { granted: 'Đã cho phép', denied: 'Đã tắt', prompt: 'Chưa hỏi', default: 'Chưa hỏi', unsupported: 'Không hỗ trợ' };

export function FeedbackDeviceBlock({ feedback, device }) {
  return (
    <Card title="Góp ý & thiết bị">
      <Rows rows={[
        ['Góp ý', feedback.threads ? <>{feedback.threads} cuộc{feedback.open ? ` · ${feedback.open} đang chờ` : ''} · <Link className="underline" to={`/admin?thread=${feedback.latest.id}`}>{feedback.latest.code}</Link></> : 'Chưa gửi'],
        ['Thiết bị', device ? [device.device, device.os, device.app_version && `App ${device.app_version}`].filter(Boolean).join(' · ') : 'Chưa ghi nhận'],
        device?.notifications && ['Thông báo', NOTIFY[device.notifications] || device.notifications],
        device?.language && ['Ngôn ngữ / múi giờ', [device.language, device.time_zone].filter(Boolean).join(' · ')],
        device && ['Ghi nhận', `${dateTime(device.seen_at)}${device.from === 'feedback' ? ' (từ góp ý)' : ''}`],
      ]} />
    </Card>
  );
}

const AUDIT_ACTIONS = {
  viewed: 'Xem trang', content_revealed: 'Mở nội dung', coach_plan_changed: 'Đổi gói chỗ HLV', coach_plan_ended: 'Kết thúc gói chỗ HLV',
  pro_granted: 'Tặng Pro', pro_ended: 'Kết thúc Pro', unlimited_set: 'Đổi không giới hạn', trial_ended: 'Kết thúc dùng thử',
  credits_added: 'Cộng lượt AI', locked: 'Khoá tài khoản', unlocked: 'Mở khoá tài khoản',
};

export function AuditBlock({ audits }) {
  return (
    <Card title="Quản trị đã làm gì với tài khoản này" aside="10 dòng gần nhất">
      {audits.length === 0 ? <p className="text-sm text-muted">Chưa có.</p> : (
        <ul className="flex flex-col gap-1 text-sm">
          {audits.map((a, i) => (
            <li key={`${a.at}-${i}`} className="flex gap-3">
              <span className="w-24 shrink-0 text-xs text-muted">{dateTime(a.at)}</span>
              <span className="min-w-0 flex-1">{a.admin} · {AUDIT_ACTIONS[a.action] || a.action}{a.after?.thread ? ` ${a.after.thread}` : ''}{a.reason ? ` · “${a.reason}”` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
